import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import {
  CalendarA11y,
  CalendarCommonModule,
  CalendarDateFormatter,
  CalendarEvent,
  CalendarModule,
  CalendarMonthModule,
  CalendarUtils,
  CalendarView,
  DateAdapter
} from 'angular-calendar';
import { Subject } from 'rxjs';
import { MemberDashboardService } from '../member-dashboard.service';
import { SharedServiceService } from '../../services/shared-service.service';

@Component({
  selector: 'app-water-log',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CalendarModule,
    CalendarCommonModule,
    CalendarMonthModule,
  ],
  providers: [
    {
      provide: DateAdapter,
      useFactory: adapterFactory,
    },
    {
      provide: CalendarDateFormatter,
      useClass: CalendarDateFormatter,
    },
    {
      provide: CalendarUtils,
      useClass: CalendarUtils,
    },
    {
      provide: CalendarA11y,
      useClass: CalendarA11y,
    },
  ],
  templateUrl: './water-log.component.html',
  styleUrl: './water-log.component.css'
})
export class WaterLogComponent implements OnInit, OnChanges {
  @Input() memberId: any;
  @Input() hydrationGoalData: any;

  view: CalendarView = CalendarView.Month;
  CalendarView = CalendarView;
  viewDate: Date = new Date();
  activeDayIsOpen: boolean = false;
  events: CalendarEvent[] = [];
  refresh = new Subject<void>();

  availableYears: number[] = [];
  selectedYear: number = new Date().getFullYear();

  currentTargetGlasses: number = 15;
  currentTargetQuantity: number = 3500;
  currentTargetUnit: string = 'ml';

  customTargetValue: number = 0;
  customTargetUnit: 'ml' | 'fl oz' = 'ml';

  hydrationLogsData: any;
  private hydrationCountByDate: Map<string, number> = new Map();

  showWaterLogModal: boolean = false;
  showAllLogs: boolean = false;
  hasGoalForDate: boolean = true;
  selectedDayDate: Date | null = null;
  selectedDateGoalGlasses: number = 0;
  selectedDateGoalUnit: string = 'ml';
  selectedDateCount: number = 0;
  selectedDateLogs: any[] = [];
  filledDrops: number[] = [];
  remainingDrops: number[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private dashboard: MemberDashboardService,
    private sharedService: SharedServiceService
  ) {}

  ngOnInit(): void {
    this.initYears();
    this.applyHydrationGoal();
    this.loadHydrationLogs();
  }

  private loadHydrationLogs(onComplete?: () => void): void {
    if (!this.memberId) return;
    const month = ('0' + (this.viewDate.getMonth() + 1)).slice(-2);
    const year = this.viewDate.getFullYear();
    this.dashboard.getHydrationLogsByMemberId(this.memberId, month, year).subscribe(response => {
      this.hydrationLogsData = response;
      this.buildHydrationCountMap(response);
      this.cdr.markForCheck();
      onComplete?.();
    });
  }

  private buildHydrationCountMap(response: any): void {
    this.hydrationCountByDate = new Map();
    const logs = response?.hydrationLogs;
    if (!Array.isArray(logs)) return;
    for (const entry of logs) {
      if (!entry?.date) continue;
      this.hydrationCountByDate.set(entry.date.substring(0, 10), entry.count || 0);
    }
  }

  private formatDateKey(date: Date): string {
    const y = date.getFullYear();
    const m = ('0' + (date.getMonth() + 1)).slice(-2);
    const d = ('0' + date.getDate()).slice(-2);
    return `${y}-${m}-${d}`;
  }

  getGlassCount(date: Date): number {
    return this.hydrationCountByDate.get(this.formatDateKey(date)) ?? 0;
  }

  private getLogsForDate(date: Date): any[] {
    const key = this.formatDateKey(date);
    const logs = this.hydrationLogsData?.hydrationLogs;
    if (!Array.isArray(logs)) return [];
    const entry = logs.find((l: any) => l?.date && l.date.substring(0, 10) === key);
    return entry?.logs ?? [];
  }

  private getGoalForDate(date: Date): { goalGlasses: number; unit: string } | null {
    const goals = this.hydrationGoalData?.result;
    if (!Array.isArray(goals)) return null;

    const key = this.formatDateKey(date);

    const activeGoal = goals.find((g: any) =>
      g.source === 'HydrationGoals' && g.startDate && key >= g.startDate.substring(0, 10)
    );
    if (activeGoal) {
      return { goalGlasses: activeGoal.goalGlasses, unit: this.fromApiUnit(activeGoal.unit) };
    }

    const historicalGoal = goals.find((g: any) =>
      g.source !== 'HydrationGoals' && g.startDate && g.endDate &&
      key >= g.startDate.substring(0, 10) && key <= g.endDate.substring(0, 10)
    );
    if (historicalGoal) {
      return { goalGlasses: historicalGoal.goalGlasses, unit: this.fromApiUnit(historicalGoal.unit) };
    }

    return null;
  }

  private fromApiUnit(unit: string): string {
    return unit === 'floz' ? 'fl oz' : unit;
  }

  private toApiUnit(unit: string): string {
    return unit === 'fl oz' ? 'floz' : unit;
  }

  private perGlassAmount(unit: string): number {
    return unit === 'fl oz' ? 8 : 240;
  }

  get selectedDateQuantity(): number {
    return this.selectedDateCount * this.perGlassAmount(this.selectedDateGoalUnit);
  }

  get selectedDateGoalQuantity(): number {
    return this.selectedDateGoalGlasses * this.perGlassAmount(this.selectedDateGoalUnit);
  }

  get perLogQuantity(): number {
    return this.perGlassAmount(this.selectedDateGoalUnit);
  }

  get isFutureSelectedDate(): boolean {
    if (!this.selectedDayDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = new Date(this.selectedDayDate);
    day.setHours(0, 0, 0, 0);
    return day > today;
  }

  padNumber(value: number): string {
    return value < 10 ? '0' + value : '' + value;
  }

  openWaterLogModal(date: Date): void {
    this.showAllLogs = false;
    this.showWaterLogModal = true;
    this.updateModalDataForDate(date);
  }

  private updateModalDataForDate(date: Date): void {
    this.selectedDayDate = date;

    const goal = this.getGoalForDate(date);
    const count = this.getGlassCount(date);
    const logs = this.getLogsForDate(date);

    this.hasGoalForDate = goal !== null || count > 0;

    if (!goal) {
      this.selectedDateGoalGlasses = 0;
      this.selectedDateGoalUnit = 'ml';
      this.selectedDateCount = count;
      this.selectedDateLogs = logs;
      this.filledDrops = Array(count).fill(0);
      this.remainingDrops = [];
      return;
    }

    this.selectedDateGoalGlasses = goal.goalGlasses;
    this.selectedDateGoalUnit = goal.unit;
    this.selectedDateCount = count;
    this.selectedDateLogs = logs;

    if (this.selectedDateGoalGlasses === 0) {
      this.filledDrops = Array(this.selectedDateCount).fill(0);
      this.remainingDrops = [];
      return;
    }

    const filled = Math.min(this.selectedDateCount, this.selectedDateGoalGlasses);
    const remaining = Math.max(this.selectedDateGoalGlasses - this.selectedDateCount, 0);
    this.filledDrops = Array(filled).fill(0);
    this.remainingDrops = Array(remaining).fill(0);
  }

  closeWaterLogModal(): void {
    this.showWaterLogModal = false;
  }

  onAddWaterLog(): void {
    if (!this.memberId || !this.selectedDayDate || this.isFutureSelectedDate) return;

    const payload = {
      memberId: this.memberId,
      date: this.formatDateKey(this.selectedDayDate),
    };

    this.dashboard.addHydrationLog(payload).subscribe(() => {
      this.loadHydrationLogs(() => this.updateModalDataForDate(this.selectedDayDate!));
    });
  }

  onDeleteWaterLog(log: any): void {
    if (!this.memberId || !this.selectedDayDate) return;

    const date = this.formatDateKey(this.selectedDayDate);
    this.dashboard.deleteHydrationLog(this.memberId, date, log.id).subscribe(() => {
      this.loadHydrationLogs(() => this.updateModalDataForDate(this.selectedDayDate!));
    });
  }

  toggleAllLogs(): void {
    this.showAllLogs = !this.showAllLogs;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hydrationGoalData']) {
      this.applyHydrationGoal();
    }
  }

  private applyHydrationGoal(): void {
    const goals = this.hydrationGoalData?.result;
    if (!Array.isArray(goals)) return;

    const today = new Date();
    const activeGoal = goals.find((goal: any) =>
      goal.source === 'HydrationGoals' &&
      goal.startDate &&
      new Date(goal.startDate) <= today
    );

    if (activeGoal) {
      this.currentTargetUnit = this.fromApiUnit(activeGoal.unit);
      this.currentTargetGlasses = activeGoal.goalGlasses;
      this.currentTargetQuantity = activeGoal.goalGlasses * this.perGlassAmount(this.currentTargetUnit);
    }
  }

  private initYears(): void {
    const current = new Date().getFullYear();
    this.availableYears = [];
    for (let y = current - 5; y <= current + 5; y++) {
      this.availableYears.push(y);
    }
    this.selectedYear = this.viewDate.getFullYear();
  }

  onYearChange(year: number): void {
    const newDate = new Date(this.viewDate);
    newDate.setFullYear(+year);
    this.viewDate = newDate;
    this.closeOpenMonthViewDay();
    this.cdr.markForCheck();
    this.loadHydrationLogs();
  }

  onMonthNavChange(): void {
    this.closeOpenMonthViewDay();
    this.selectedYear = this.viewDate.getFullYear();
    this.cdr.markForCheck();
    this.loadHydrationLogs();
  }

  closeOpenMonthViewDay(): void {
    this.activeDayIsOpen = false;
  }

  private customTargetStep(): number {
    return this.customTargetUnit === 'ml' ? 240 : 8;
  }

  incrementCustomTarget(): void {
    this.customTargetValue += this.customTargetStep();
  }

  decrementCustomTarget(): void {
    this.customTargetValue = Math.max(0, this.customTargetValue - this.customTargetStep());
  }

  setUnit(unit: 'ml' | 'fl oz'): void {
    if (this.customTargetUnit === unit) return;
    this.customTargetUnit = unit;
    this.customTargetValue = 0;
  }

  resetCustomTarget(): void {
    this.customTargetValue = 0;
  }

  onAddCustomTarget(): void {
    if (!this.memberId || this.customTargetValue <= 0) return;

    const payload = {
      memberId: this.memberId,
      loggedBy: this.sharedService.getLoggedInUser()?.id,
      goalGlasses: Math.round(this.customTargetValue / this.customTargetStep()),
      unit: this.toApiUnit(this.customTargetUnit),
    };

    this.dashboard.addHydrationGoal(payload).subscribe(() => {
      this.resetCustomTarget();
      this.refreshHydrationGoal();
    });
  }

  private refreshHydrationGoal(): void {
    if (!this.memberId) return;
    this.dashboard.getHydrationGoalsByMemberId(this.memberId).subscribe(response => {
      this.hydrationGoalData = response;
      this.applyHydrationGoal();
      this.cdr.markForCheck();
    });
  }

  dayClicked({ date }: { date: Date; events: CalendarEvent[] }): void {
    this.openWaterLogModal(date);
  }
}
